import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "../../context/ThemeContext";
import { useUserStore } from "../../store/userStore";
import api from "../../utils/api";
import { qk } from "../../lib/queryKeys";
import { NotificationPrefsSection } from "../../components/users/NotificationPrefsSection";
import { PushNotificationCard } from "../../components/users/PushNotificationCard";
import { DEFAULT_NOTIFICATION_PREFS, type NotificationPrefs } from "../../features/users/notificationPrefs";
import { useUserNotificationPrefs } from "../../features/users/queries";

/**
 * Configuración (app «ajustes»). Por ahora una sección, Alertas: lo que antes
 * estaba en la pestaña «Alertas» de Mi perfil (Daniel, 26-oct). Sólo se movió
 * la UI; el funcionamiento de las alertas no cambia (lo define Daniel aparte).
 */
export default function SettingsPage() {
  const { isDarkMode } = useTheme();
  const user = useUserStore((s) => s.user);
  const setUser = useUserStore((s) => s.setUser);
  const qc = useQueryClient();
  const prefsQuery = useUserNotificationPrefs(user?.id);
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<"ok" | "error" | null>(null);

  useEffect(() => {
    if (prefsQuery.data) setPrefs(prefsQuery.data);
  }, [prefsQuery.data]);

  const dirty = !!prefsQuery.data && JSON.stringify(prefs) !== JSON.stringify(prefsQuery.data);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    setSaved(null);
    try {
      // PUT /user sólo toca los campos que recibe (usersController.putUser).
      await api.put("/user", { userID: user.id, user: { notification_prefs: prefs } }, { timeout: 60000 });
      setUser({ ...user, notification_prefs: prefs });
      qc.setQueryData(qk.users.notificationPrefs(user.id), prefs);
      setSaved("ok");
    } catch {
      setSaved("error");
    } finally {
      setSaving(false);
    }
  };

  const card = isDarkMode ? "bg-dark-card border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-6 grid gap-6 md:grid-cols-[13rem_1fr]">
        <nav aria-label="Secciones de configuración" className="md:sticky md:top-6 self-start">
          <h1 className={`text-lg font-semibold mb-3 ${text}`}>Configuración</h1>
          <ul className="flex md:flex-col gap-1">
            <li>
              <span aria-current="page" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary-500/15 text-primary-500">
                <i className="ri-notification-3-line" aria-hidden="true" />
                Alertas
              </span>
            </li>
          </ul>
        </nav>

        <section aria-labelledby="settings-alerts" className="space-y-4 min-w-0">
          <div>
            <h2 id="settings-alerts" className={`text-base font-semibold ${text}`}>Alertas</h2>
            <p className={`text-sm ${muted}`}>Qué avisos de tus unidades recibes y cómo te llegan.</p>
          </div>

          <PushNotificationCard isDarkMode={isDarkMode} />

          <div className={`rounded-xl border p-4 ${card}`}>
            {prefsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" aria-label="Cargando" />
              </div>
            ) : (
              <NotificationPrefsSection value={prefs} onChange={setPrefs} isDarkMode={isDarkMode} defaultExpanded collapsible={false} />
            )}
            <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
              {saved === "ok" && !dirty && <span className="text-sm text-green-500">Guardado</span>}
              {saved === "error" && <span className="text-sm text-red-500">No se pudo guardar. Intenta de nuevo.</span>}
              <button
                type="button"
                disabled={!dirty || saving}
                onClick={() => prefsQuery.data && setPrefs(prefsQuery.data)}
                className={`text-sm px-4 py-2 rounded-lg border disabled:opacity-40 ${isDarkMode ? "border-dark-border text-white" : "border-gray-300"}`}
              >
                Descartar
              </button>
              <button
                type="button"
                disabled={!dirty || saving}
                onClick={save}
                className="text-sm px-4 py-2 rounded-lg bg-primary-500 text-gray-900 font-medium disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
